import { Role } from '~/api/graphql';
import { useCurrentAgencyUser } from '~/components/current-user';
import { useVisibilityEntitlement } from './use-visibility-entitlement';

/**
 * Returns true iff the current viewer should see the visibility editor for
 * an entity owned by `ownerAgencyUserId`. The agency must have the
 * VISIBILITY_CONTROL entitlement; on top of that, the viewer must be one of:
 *
 *   1. The owner of the entity (creator).
 *   2. An agency admin (carries the ADMIN role on AgencyUser). Admins can
 *      override visibility on any row in their agency.
 *   3. Anyone, when the owner is unknown (legacy data pre-dating the
 *      `createdByUserId` field). Hiding the control on those rows would lock
 *      the visibility down forever; the backend still enforces who can
 *      actually save the change.
 *
 * Use this on EDIT surfaces (existing entity). For CREATION surfaces the
 * current user is by definition the creator, so call useVisibilityEntitlement
 * directly there.
 */
export const useVisibilityEditAllowed = (ownerAgencyUserId: string | null | undefined): boolean => {
  const entitled = useVisibilityEntitlement();
  const agencyUser = useCurrentAgencyUser();
  if (!entitled) return false;
  if (!ownerAgencyUserId) return true;
  if (ownerAgencyUserId === agencyUser.id) return true;
  return agencyUser.roles.includes(Role.Admin);
};
